// Serial (follow-up) concordance — EXPLORATORY sub-study, pre-registered per arm
// (OSF_serial_concordance_pregnancy_6SKZ2.md / ..._preconception_9T6BS.md).
//
// Scores a SECOND concordance tier at the already-approved T0+4-8wk re-check.
// Everything here keys on the VISIT (followup_results.id), never the case: these
// are repeated measures and must never be pooled into the primary endpoint, which
// is defined as one independent decision per case.

import { supabase } from '@/integrations/supabase/client';
import { parseDoseCombination, calculateTierConcordance } from '@/components/dose-utils';
import { callRailwayAPI } from '@/components/assessment/useRailwayAPI';
import type { RailwayApiError } from '@/components/assessment/types';
import type { FollowupArm } from '@/lib/followups-shared';

export const serialArmConfig = {
  pregnancy: {
    caseColumns:
      'id,created_at,gestational_week,weight,age,thyroidectomy,hypothyroidism_before,miscarriage,ivf,twin,ft4,tai,tai_handling_choice_naive_unknown,optional_treatment_choice,iron_supplements,calcium_supplements',
    clinicianResponses: 'followup_clinician_responses',
    apiResponses: 'followup_api_responses',
    concordanceResults: 'followup_concordance_results',
    railwayUrl: import.meta.env.VITE_RAILWAY_PREGNANCY_URL as string,
    payloadKey: 'patient_data',
  },
  preconception: {
    caseColumns:
      'id,created_at,weight,age,thyroidectomy,hypothyroidism_before,miscarriage_history,planning_ivf,ft4,tai_status,tai_handling_choice_naive_unknown,optional_treatment_choice,iron_supplements,calcium_supplements',
    clinicianResponses: 'preconception_followup_clinician_responses',
    apiResponses: 'preconception_followup_api_responses',
    concordanceResults: 'preconception_followup_concordance_results',
    railwayUrl: import.meta.env.VITE_RAILWAY_PRECONCEPTION_URL as string,
    payloadKey: 'patient',
  },
} as const;

/**
 * Rebuilds the arm's Railway payload from the STORED index case, overriding only
 * what the re-check changed.
 *
 * The stored column names differ from the form's field names (`ft4` vs `fT4`,
 * `tai`/`tai_status` vs `TAI`, flat supplement columns vs a nested `medications`
 * object). The index path never hits this because it posts the live form object;
 * the follow-up must map deliberately, and a silent mismatch here would not throw
 * — it would just score the wrong tier.
 *
 * Overridden vs index:
 *   tsh              -> the re-check TSH
 *   gestational_week -> index GA + elapsed interval, floor()ed to COMPLETED weeks
 *                       (obstetric convention; matches v_followup_visits)
 *   on_treatment     -> true, always: she was dosed at index, so the algorithm
 *                       resolves to dose-adjustment rather than the naive path
 *   previous_dose    -> the dose actually in effect at the re-check
 *
 * Weight is reused from the index case and not re-measured — it informs only the
 * naive/de-novo strategy, which cannot apply here.
 */
export function buildFollowupPayload(
  arm: FollowupArm,
  caseRow: Record<string, unknown>,
  followupTsh: number,
  intervalWeeks: number,
  previousDose: number | null,
): Record<string, unknown> {
  const shared = {
    tsh: followupTsh,
    weight: caseRow.weight,
    age: caseRow.age,
    on_treatment: true,
    previous_dose: previousDose,
    thyroidectomy: caseRow.thyroidectomy,
    fT4: caseRow.ft4,
    tai_handling_choice_naive_unknown: caseRow.tai_handling_choice_naive_unknown,
    optional_treatment_choice: caseRow.optional_treatment_choice,
    hypothyroidism_before: caseRow.hypothyroidism_before,
    medications: {
      iron_supplements: caseRow.iron_supplements,
      calcium_supplements: caseRow.calcium_supplements,
    },
  };

  if (arm === 'pregnancy') {
    return {
      ...shared,
      gestational_week: Math.floor(((caseRow.gestational_week as number) ?? 0) + intervalWeeks),
      miscarriage: caseRow.miscarriage,
      ivf: caseRow.ivf,
      twin: caseRow.twin,
      TAI: caseRow.tai,
      // Thyroidectomy-for-cancer is out of study scope and cannot be enrolled, so
      // any stored thyroidectomy case is benign by construction.
      thyroidectomy_reason: caseRow.thyroidectomy ? 'benign' : null,
    };
  }
  return {
    ...shared,
    miscarriage_history: caseRow.miscarriage_history,
    planning_ivf: caseRow.planning_ivf,
    TAI: caseRow.tai_status,
  };
}

export interface SerialProposal {
  recommended_dose: string;
  alternative_dose: string;
}

export type SerialOutcome =
  | { status: 'scored'; tier: number }
  | { status: 'api_failed'; userMessage: string }
  | { status: 'skipped' };

/**
 * Scores the serial concordance for one visit.
 *
 * ORDERING IS DELIBERATE: the caller must have already committed the follow-up
 * TSH (the REGISTERED primary outcome of the parent spin-off) before calling
 * this. Everything here is exploratory, so an algorithm failure records an error
 * row and returns — it must never roll back or block the registered value. The
 * visit and the proposal survive regardless.
 */
export async function scoreSerialConcordance(
  arm: FollowupArm,
  followupId: string,
  caseId: string,
  clinicianId: string,
  proposal: SerialProposal,
  followupTsh: number,
  intervalWeeks: number,
  previousDose: number | null,
  responseTimeSeconds?: number,
): Promise<SerialOutcome> {
  const cfg = serialArmConfig[arm];
  const db = supabase as any;

  const { data: caseRow, error: caseErr } = await db
    .from(arm === 'pregnancy' ? 'clinical_cases' : 'preconception_cases')
    .select(cfg.caseColumns)
    .eq('id', caseId)
    .single();
  if (caseErr || !caseRow) {
    console.error('[serial] index case fetch failed', caseErr);
    return { status: 'api_failed', userMessage: 'Αδυναμία ανάκτησης της αρχικής εγγραφής.' };
  }

  const primaryDose = parseDoseCombination(proposal.recommended_dose).dailyAverage;

  const { data: responseRow, error: respErr } = await db
    .from(cfg.clinicianResponses)
    .upsert(
      {
        followup_id: followupId,
        case_id: caseId,
        clinician_id: clinicianId,
        recommended_dose: proposal.recommended_dose,
        alternative_dose: proposal.alternative_dose || null,
        response_time_seconds: responseTimeSeconds ?? null,
      },
      { onConflict: 'followup_id' },
    )
    .select()
    .single();
  if (respErr) {
    console.error('[serial] proposal save failed', respErr);
    return { status: 'api_failed', userMessage: 'Αδυναμία αποθήκευσης της δοσολογικής πρότασης.' };
  }

  try {
    const railwayData = await callRailwayAPI(
      buildFollowupPayload(arm, caseRow, followupTsh, intervalWeeks, previousDose),
      { url: cfg.railwayUrl, payloadKey: cfg.payloadKey },
    );

    const { data: apiRow, error: apiErr } = await db
      .from(cfg.apiResponses)
      .upsert(
        {
          followup_id: followupId,
          case_id: caseId,
          api_recommended_dose: railwayData.daily_dose,
          api_treatment_recommendation: railwayData.message ?? null,
          api_response_data: railwayData,
          strategy_used: railwayData.strategy_used ?? null,
          api_error: null,
        },
        { onConflict: 'followup_id' },
      )
      .select()
      .single();
    if (apiErr) throw apiErr;

    const tierResult = calculateTierConcordance(
      proposal.recommended_dose,
      proposal.alternative_dose || '',
      railwayData.daily_dose,
    );
    const apiDose = railwayData.daily_dose || 0;
    const secondaryDose = proposal.alternative_dose
      ? parseDoseCombination(proposal.alternative_dose).dailyAverage
      : null;

    const { error: concErr } = await db.from(cfg.concordanceResults).upsert(
      {
        followup_id: followupId,
        case_id: caseId,
        clinician_response_id: responseRow.id,
        api_response_id: apiRow.id,
        tier: tierResult.tier,
        clinical_dose_primary: primaryDose,
        clinical_dose_secondary: secondaryDose,
        dose_concordance_percentage:
          tierResult.tier === 1 ? 100 : tierResult.tier === 2 ? 95 : tierResult.tier === 3 ? 80 : 50,
        absolute_dose_difference: Math.abs(primaryDose - apiDose),
        percentage_difference: apiDose !== 0 ? ((primaryDose - apiDose) / apiDose) * 100 : 0,
        treatment_concordance: tierResult.isAcceptable,
      },
      { onConflict: 'followup_id' },
    );
    if (concErr) throw concErr;

    return { status: 'scored', tier: tierResult.tier };
  } catch (err) {
    console.error('[serial] scoring failed', err);
    const apiErr = err as Partial<RailwayApiError>;
    const label = `${apiErr?.errorCategory ?? 'UNKNOWN'}: ${apiErr?.message ?? 'Unknown error'}`;

    // Record why it failed; the visit and the proposal are already committed.
    const { error: errRowErr } = await db.from(cfg.apiResponses).upsert(
      {
        followup_id: followupId,
        case_id: caseId,
        api_error: label,
        api_response_data: {
          error_details: { message: apiErr?.message, category: apiErr?.errorCategory },
        },
      },
      { onConflict: 'followup_id' },
    );
    if (errRowErr) console.error('[serial] error-row write failed', errRowErr);

    return {
      status: 'api_failed',
      userMessage: apiErr?.userMessage ?? 'Ο αλγόριθμος δεν απάντησε.',
    };
  }
}
